import React, { useCallback, useContext } from 'react';
import DOMPurify from 'dompurify';
import classNames from 'classnames';

import DocumentContext from '../contexts/DocumentContext.context';
import { SignInContext } from '@modules/users';

import { StatusLabel } from '@shared/components';
import { useScrollContainer } from '@shared/hooks/useScrollContainer.hook';
import { getDocumentConfig } from '../helpers/getDocumentConfig.helper';
import useDiffComputation from '../hooks/useDiffComputation.hook';
import { DOCUMENT_HTML_ATTRIBUTES } from '../constants';
import { resolveStaticContentLinkTarget } from '../helpers/scrollContent/staticContentLinkTarget';

const Changes = () => {
  const { organization } = useContext(SignInContext);
  const { version, refContentNode } = useContext(DocumentContext);
  const { landscape } = getDocumentConfig(version?.config);

  const { diffContent, errorMessage, blobError } = useDiffComputation();

  const scrollContainer = useScrollContainer();

  const handleRefClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = resolveStaticContentLinkTarget(e.target as EventTarget);
      if (!target) return;

      e.preventDefault();
      scrollContainer({
        containerRef: refContentNode,
        // Reference/heading links carry `id` in diff output; scroll links carry `data-id`.
        attribute:
          target.kind === 'ref' ? DOCUMENT_HTML_ATTRIBUTES.DEFAULT_ID : DOCUMENT_HTML_ATTRIBUTES.CUSTOM_UNIQUE_ID,
        value: target.value,
      });
    },
    [scrollContainer, refContentNode]
  );

  return (
    <div className="document-content-container">
      <StatusLabel
        data={diffContent}
        error={blobError || errorMessage}
        loadingMessage="Loading changes..."
        errorMessage={errorMessage || 'Failed to load changes.'}
      >
        <div
          onClick={handleRefClick}
          className={classNames(
            'document-content static-content changes-content',
            `custom-${organization.stylesConfig.styleVariant}`,
            { 'landscape-mode': landscape }
          )}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(diffContent || '', { ADD_ATTR: ['target', 'data-target-id'] }),
          }}
        />
      </StatusLabel>
    </div>
  );
};

export default Changes;
